// Group overview pages (owner, 2026-10-21): Online store, Apps and Settings are one sidebar link each,
// opening a page that lists every page of the group the role can open as a card (icon, name, one
// line on what it does). The overview's own nav item (`<group>Home`) is the first item of its group.
import { createRoute, type AnyRoute } from "@tanstack/react-router";
import { i18n, t, type PlainKey } from "@ady/i18n";
import { Icon, type IconName } from "@ady/ui";
import { MigrateBanner } from "../modules/storefront/MigrateParts";
import { AppLink } from "./AppLink";
import { PlanLockBadge, usePlanLocks } from "./ControlCentre";
import { SECTION_BARS } from "./GroupBar";
import { useShell } from "./context";
import { IDE_TAB_NAME, openIdeTab, THEME_TAB_NAME } from "./ideWindow";
import { PageHeader } from "./PageHeader";
import { can, findItem, hrefFor, iconFor, visibleNav } from "./nav";

/** Group id → its overview nav item id. */
export const HUBS: Record<string, string> = {
  store: "storeHome",
  apps: "appsHome",
  settings: "settingsHome",
};

/** Pages reached from another card, not a card of their own: the Page editor opens from Pages, blog
 * and forms → Pages (owner, 2026-10-26); its address still works. */
const NO_CARD = ["pageEditor"];

/** Cards named for their whole tab row rather than its first page (owner, 2026-10-27/29). */
const CARD_LABEL: Record<string, PlainKey> = { apiKeys: "nav.items.developers" };
const CARD_TEXT: Record<string, string> = { apiKeys: "developers" };
const CARD_ICON: Record<string, IconName> = { apiKeys: "code" };

/** Cards that open their page in its own browser tab (the tab's name, so a second click reuses it). */
const TAB_NAMES: Record<string, string> = {
  codeEditor: IDE_TAB_NAME,
  theme: THEME_TAB_NAME,
};

function GroupHub({ group }: { group: string }) {
  const { membership, orgSlug, storeSlug } = useShell();
  const lockFor = usePlanLocks();
  // Pages that are later tabs of a section bar open from the bar's first page, not a card.
  const bar = SECTION_BARS[group];
  const tabbed = [bar?.tabs ?? [], ...(bar?.rows ?? [])].flatMap((r) => r.slice(1));
  const items = (visibleNav(membership).find((g) => g.id === group)?.items ?? []).filter(
    (i) => i.id !== HUBS[group] && !tabbed.includes(i.id) && !NO_CARD.includes(i.id),
  );
  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t(findItem(HUBS[group] ?? "")?.label ?? "nav.groups.store")}
        lead={i18n.tDynamic(`hubs.leads.${group}`, "hubs.leads.store")}
      />
      {/* Apps: move a store across from another platform (owner, 2026-10-27; D-075). */}
      {group === "apps" && can(membership, ["products.bulk"]) ? <MigrateBanner /> : null}
      <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {items.map((item) => (
          <li key={item.id}>
            <AppLink
              href={hrefFor(item, orgSlug, storeSlug)}
              // The Code and Theme editors open in their own tabs, like desktop tools (owner,
              // 2026-10-26/27).
              onClick={
                TAB_NAMES[item.id]
                  ? (e) => {
                      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
                        return;
                      if (openIdeTab(hrefFor(item, orgSlug, storeSlug), TAB_NAMES[item.id]))
                        e.preventDefault();
                    }
                  : undefined
              }
              className="flex h-full min-h-32 flex-col gap-2 rounded-xs border border-card bg-surface p-4 text-fg no-underline hover:bg-hover"
            >
              <span className="flex items-start">
                <span className="flex size-10 flex-none items-center justify-center rounded-xs border border-card bg-surface">
                  <Icon name={CARD_ICON[item.id] ?? iconFor(item)} gradient />
                </span>
                {/* The crown of the plan that unlocks it, as on the sidebar (owner, 2026-10-27). */}
                {lockFor(item.id) ? <PlanLockBadge unlock={lockFor(item.id)!.unlock} /> : null}
              </span>
              <span className="ady-hover-label text-md font-semibold">
                {t(CARD_LABEL[item.id] ?? item.label)}
              </span>
              <span className="text-sm text-fg-secondary">
                {i18n.tDynamic(`hubs.cards.${CARD_TEXT[item.id] ?? item.id}`, "hubs.cardFallback")}
              </span>
            </AppLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function hubRoutes(appRoute: AnyRoute): AnyRoute[] {
  return Object.keys(HUBS).map((group) =>
    createRoute({
      getParentRoute: () => appRoute,
      path: `/$org/$store/${group}`,
      component: () => <GroupHub group={group} />,
    }),
  );
}
